// src/pages/Vehicles.tsx
import React, { useEffect, useState } from "react";
import { getVehicles } from "../services/api";
import { useNavigate } from "react-router-dom";

interface Vehicle {
  id: number;
  vehicleNo: string;
  vehicleType: string;
  balance: number;
  ownerName: string;
  ownerPhone: string;
  monthlyFee: number;
  status: string;
}

const Vehicles: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const data = await getVehicles();
      setVehicles(data);
    } catch (error) {
      console.error("Error fetching vehicles", error);
    }
  };

  const filteredVehicles = vehicles.filter((v) => {
    const matchesSearch = v.vehicleNo.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterType ? v.vehicleType === filterType : true;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen w-full bg-gray-100 flex flex-col">
      {/* Header bar */}
      <div className="flex justify-between items-center bg-white shadow p-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/")}
            className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 transition text-sm md:text-base"
          >
            Home
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Vehicles</h1>
        </div>
        <button
          onClick={() => navigate("/register-vehicle")}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition text-sm md:text-base"
        >
          Register New Vehicle
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 p-4 md:p-6 space-y-6">
        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-start gap-4">
          <input
            type="text"
            placeholder="Search by Vehicle No"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded px-3 py-2 w-full md:w-1/3 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border rounded px-3 py-2 w-full md:w-1/4 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Types</option>
            <option value="FOUR_WHEELER">Four Wheeler</option>
            <option value="SIX_WHEELER">Six Wheeler</option>
            <option value="EIGHT_WHEELER">Eight Wheeler</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        {/* Vehicles Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded">
            {/* Header only visible on md+ */}
            <thead className="hidden md:table-header-group">
              <tr className="bg-gray-100 text-left">
                <th className="py-2 px-4">Vehicle No</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className="border-b cursor-pointer transition duration-200 transform hover:bg-blue-50 hover:shadow-md md:table-row flex flex-col md:flex-row mb-4 md:mb-0 p-4 md:p-0 rounded md:rounded-none"
                >
                  <td className="py-2 px-0 md:px-4 font-medium md:font-normal">
                    <span className="md:hidden text-gray-500">Vehicle No: </span>
                    {vehicle.vehicleNo}
                  </td>
                  <td className="py-2 px-0 md:px-4">
                    <span className="md:hidden text-gray-500">Type: </span>
                    {vehicle.vehicleType}
                  </td>
                  <td className="py-2 px-0 md:px-4">
                    <span className="md:hidden text-gray-500">Balance: </span>
                    {vehicle.balance}
                  </td>
                </tr>
              ))}
              {filteredVehicles.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    No vehicles found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Vehicles;
